import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {AuthenticatedBaseComponent} from '../../../authenticated-base.component';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {Backend, PERMISSION} from '@generated-backend-types';
import {of, switchMap, takeUntil, throwError} from 'rxjs';
import {ContentHeaderService} from '../../../../services/content-header.service';
import {TitleService} from '../../../../services/title.service';
import {ActivatedRoute} from '@angular/router';
import {FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {ReadOnlyStaticComponent} from '../../../../components/form-inputs/read-only-static/read-only-static.component';
import {FormGroupComponent} from '../../../../components/form-inputs/form-group/form-group.component';
import {UserSessionService} from '../../../../services/user-session.service';
import {NotEnoughPermissionsError} from '../../../../errors/NotEnoughPermissionsError';
import {UserService} from '../../../../services/api/administration/user.service';
import {HttpErrorResponse} from '@angular/common/http';
import {ToastrService} from 'ngx-toastr';
import {FormErrorService} from '../../../../services/form-error.service';
import {
  MultiSelectEnumComponent
} from '../../../../components/form-inputs/multi-select-enum/multi-select-enum.component';
import ReducedUser = Backend.App.Resources.Dashboard.Administration.ReducedUser;
import RoleEnum = Backend.App.Enums.Auth.RoleEnum;

@Component({
  selector: 'app-authenticated-administration-users-edit',
  templateUrl: './edit.component.html',
  imports: [
    FormsModule,
    ReadOnlyStaticComponent,
    TranslatePipe,
    FormGroupComponent,
    ReactiveFormsModule,
    MultiSelectEnumComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditComponent extends AuthenticatedBaseComponent implements OnInit {
  public user?: ReducedUser;

  public form = this.fb.group({
    color: this.fb.control('', [Validators.required]),
    roles: this.fb.control<RoleEnum[]>([], [Validators.required])
  });
  protected readonly RoleEnum = RoleEnum;

  public constructor(
    protected override readonly cdr: ChangeDetectorRef,
    protected override readonly translate: TranslateService,
    protected override readonly contentHeaderService: ContentHeaderService,
    protected override readonly titleService: TitleService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly fb: NonNullableFormBuilder,
    private readonly userSessionService: UserSessionService,
    private readonly userService: UserService,
    private readonly toastrService: ToastrService,
    private readonly formErrorService: FormErrorService,
  ) {
    super(cdr, translate, contentHeaderService, titleService);
  }

  public ngOnInit() {
    this.activatedRoute.data.pipe(takeUntil(this.onDestroy$)).subscribe(data => {
      const user: ReducedUser | undefined = data['user'];

      if (user) {
        this.user = user;

        this.form.patchValue({
          ...user,
          roles: user.roles?.map(role => role.key as RoleEnum)
        });

        this.cdr.markForCheck();
      }

      this.titleService.setTitle('authenticated.administration.users.edit.title');
    });
  }

  public save() {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();

    if (!this.form.valid || !this.user?.id) {
      return;
    }

    const data = this.form.getRawValue();

    of(this.user.id).pipe(
      switchMap(id => {
        if (!this.userSessionService.hasAllPermission([PERMISSION.USER_UPDATE_ALL])) {
          throwError(() => new NotEnoughPermissionsError());
        }

        return this.userService.update(id, data);
      })
    ).subscribe({
      next: res => {
        this.form.markAsPristine();
        this.form.markAsUntouched();

        this.toastrService.success(this.translate.instant(`authenticated.administration.users.edit.success`));
        this.user = res.data;

        this.cdr.markForCheck();
      },
      error: err => {
        if (err instanceof NotEnoughPermissionsError) {
          this.toastrService.error(this.translate.instant('misc.not_enough_permissions_error'));
        } else if (err instanceof HttpErrorResponse) {
          this.form.markAllAsTouched();
          this.form.updateValueAndValidity({onlySelf: false, emitEvent: true});
          this.formErrorService.mapErrorToForm(this.form, err);
        } else {
          this.toastrService.error(this.translate.instant(`authenticated.administration.users..error`));
        }
      },
    })

    this.cdr.markForCheck();
  }
}
